import { Injectable, inject } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { PaymentPromisePageDto, PaymentPlanPageDto, PaymentService } from '../payment.service';
import {
  PaymentPromiseDto,
  PaymentPlanCommand,
  PaymentPlanDto,
  PaymentPromiseStatus,
  CreatePromiseCommand,
} from '../models/payment.model';
import { MockDataLoader } from './mock-data-loader.service';
import { PaymentMockStateService } from './payment-mock-state.service';
import { todayBusinessDate } from './business-date.util';

interface PromisesFixture { items: PaymentPromiseDto[] }
interface PlansFixture    { items: PaymentPlanDto[]    }

@Injectable()
export class PaymentMockService extends PaymentService {
  private readonly mockData = inject(MockDataLoader);
  private readonly state = inject(PaymentMockStateService);

  getPromises(caseId?: string): Observable<PaymentPromisePageDto> {
    const today = todayBusinessDate();
    return this.mockData.load<PromisesFixture>('payment-promises').pipe(
      map(f => {
        const all = [...f.items, ...this.state.getPromises()];
        const filtered = caseId ? all.filter(p => p.caseId === caseId) : all;
        const withBroken = filtered.map(p =>
          p.status === 'ACTIVE' && p.promiseDate < today
            ? { ...p, status: 'BROKEN' as PaymentPromiseStatus }
            : p
        );
        const sorted = withBroken.sort((a, b) => a.promiseDate.localeCompare(b.promiseDate));
        return { items: sorted };
      }),
    );
  }

  createPromise(command: CreatePromiseCommand): Observable<PaymentPromiseDto> {
    const today = todayBusinessDate();
    const autoStatus: PaymentPromiseStatus =
      command.promiseDate < today ? 'BROKEN' : 'ACTIVE';
    const dto: PaymentPromiseDto = {
      promiseId:          `PROM-NEW-${Date.now()}`,
      caseId:             command.caseId,
      caseReference:      '',
      debtorId:           '',
      debtorName:         '',
      promiseDate:        command.promiseDate,
      promiseAmount:      command.promiseAmount,
      currency:           command.currency,
      channel:            command.channel,
      status:             autoStatus,
      createdByAgentId:   'AGT-001',
      createdByAgentName: 'Agent (session)',
      createdAt:          new Date().toISOString(),
      notes:              command.notes,
    };
    this.state.addPromise(dto);
    return of(dto).pipe(delay(300));
  }

  getPaymentPlan(caseId: string): Observable<PaymentPlanDto | null> {
    return this.mockData.load<PlansFixture>('payment-plans').pipe(
      map(f => this.state.getPaymentPlan(caseId) ?? f.items.find(p => p.caseId === caseId) ?? null),
    );
  }

  createPaymentPlan(command: PaymentPlanCommand): Observable<PaymentPlanDto> {
    return of(this.state.createPaymentPlan(command)).pipe(delay(300));
  }
}
